import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Loader2 } from 'lucide-react';
import { API_URL } from '../../services/api';

const CONTACT_EMAIL = 'asaphowou@gmail.com';

const Contact: React.FC = () => {
    const [form, setForm] = useState({
        name: '',
        email: '',
        subject: '',
        message: '',
    });
    const [sending, setSending] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');

    const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { id, value } = e.target;
        setForm((prev) => ({ ...prev, [id]: value }));
    };

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSending(true);
        setError('');
        setSuccess('');
        try {
            const res = await fetch(`${API_URL}/support`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: form.name.trim(),
                    email: form.email.trim(),
                    subject: form.subject.trim(),
                    body: form.message.trim(),
                    source: 'contact',
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || 'Envoi impossible');
            setSuccess('Message envoyé. Notre équipe vous répondra rapidement.');
            setForm({ name: '', email: '', subject: '', message: '' });
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Erreur lors de l’envoi');
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="contact" className="py-20 bg-white">
            <div className="container mx-auto px-4">
                <div className="text-center mb-16">
                    <h4 className="text-blue-600 font-semibold uppercase tracking-wider mb-2">
                        Contactez-nous
                    </h4>
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                        Une Question ? Écrivez-nous
                    </h2>
                </div>

                <div className="flex flex-col lg:flex-row gap-12">
                    <div className="lg:w-1/3 space-y-8">
                        <div className="bg-blue-50 p-8 rounded-2xl">
                            <div className="flex items-center space-x-4 mb-4">
                                <div className="bg-blue-100 p-3 rounded-full text-blue-600">
                                    <Phone size={24} />
                                </div>
                                <div>
                                    <h5 className="text-lg font-bold text-gray-900">Appelez-nous</h5>
                                    <p className="text-gray-600">+225 07 10 14 59 75</p>
                                </div>
                            </div>
                        </div>
                        <div className="bg-blue-50 p-8 rounded-2xl">
                            <div className="flex items-center space-x-4 mb-4">
                                <div className="bg-blue-100 p-3 rounded-full text-blue-600">
                                    <Mail size={24} />
                                </div>
                                <div>
                                    <h5 className="text-lg font-bold text-gray-900">Email</h5>
                                    <a
                                        href={`mailto:${CONTACT_EMAIL}`}
                                        className="text-gray-600 hover:text-blue-600 break-all"
                                    >
                                        {CONTACT_EMAIL}
                                    </a>
                                </div>
                            </div>
                        </div>
                        <div className="bg-blue-50 p-8 rounded-2xl">
                            <div className="flex items-center space-x-4 mb-4">
                                <div className="bg-blue-100 p-3 rounded-full text-blue-600">
                                    <MapPin size={24} />
                                </div>
                                <div>
                                    <h5 className="text-lg font-bold text-gray-900">Adresse</h5>
                                    <p className="text-gray-600">Abidjan, Côte d&apos;Ivoire</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="lg:w-2/3">
                        <form
                            onSubmit={onSubmit}
                            className="bg-gray-50 p-8 md:p-12 rounded-3xl shadow-sm border border-gray-100"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                <div>
                                    <label
                                        htmlFor="name"
                                        className="block text-sm font-medium text-gray-700 mb-2"
                                    >
                                        Nom complet
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        required
                                        value={form.name}
                                        onChange={onChange}
                                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                                        placeholder="Votre nom"
                                    />
                                </div>
                                <div>
                                    <label
                                        htmlFor="email"
                                        className="block text-sm font-medium text-gray-700 mb-2"
                                    >
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        required
                                        value={form.email}
                                        onChange={onChange}
                                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                                        placeholder="votre@email.com"
                                    />
                                </div>
                            </div>
                            <div className="mb-6">
                                <label
                                    htmlFor="subject"
                                    className="block text-sm font-medium text-gray-700 mb-2"
                                >
                                    Sujet
                                </label>
                                <input
                                    type="text"
                                    id="subject"
                                    required
                                    value={form.subject}
                                    onChange={onChange}
                                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                                    placeholder="Sujet de votre message"
                                />
                            </div>
                            <div className="mb-8">
                                <label
                                    htmlFor="message"
                                    className="block text-sm font-medium text-gray-700 mb-2"
                                >
                                    Message
                                </label>
                                <textarea
                                    id="message"
                                    rows={5}
                                    required
                                    value={form.message}
                                    onChange={onChange}
                                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                                    placeholder="Comment pouvons-nous vous aider ?"
                                />
                            </div>
                            {error && (
                                <p className="mb-4 text-sm text-red-600 bg-red-50 px-4 py-2 rounded-lg">
                                    {error}
                                </p>
                            )}
                            {success && (
                                <p className="mb-4 text-sm text-emerald-700 bg-emerald-50 px-4 py-2 rounded-lg">
                                    {success}
                                </p>
                            )}
                            <button
                                type="submit"
                                disabled={sending}
                                className="w-full bg-blue-600 text-white font-bold py-4 rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-60"
                            >
                                {sending ? (
                                    <>
                                        <Loader2 className="w-5 h-5 animate-spin" />
                                        <span>Envoi…</span>
                                    </>
                                ) : (
                                    <>
                                        <span>Envoyer le message</span>
                                        <Send size={20} />
                                    </>
                                )}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Contact;
